import React, { useEffect, useRef, useState } from "react";
import "./SoilInvestigation.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/SIgaller1.jpeg";
import gallery2 from "../assets/gallery/SIgaller2.jpeg";
import gallery3 from "../assets/gallery/SIgaller3.jpeg";
import gallery4 from "../assets/gallery/SIgaller4.jpeg";
import gallery5 from "../assets/gallery/SIgaller5.jpeg";
import gallery6 from "../assets/gallery/SIgaller6.jpeg";
import gallery7 from "../assets/gallery/SIgaller7.jpeg";
import gallery8 from "../assets/gallery/SIgaller8.jpeg";
import gallery9 from "../assets/gallery/SIgaller9.jpeg";
import gallery10 from "../assets/gallery/SIgaller10.jpeg";
import overviewImg from "../assets/gallery/si-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
  { src: gallery9},
  { src: gallery10},
];

/* ── Main SoilInvestigation Component ── */
const SoilInvestigation = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Borehole Drilling",
      desc: "Rotary and percussion drilling to retrieve soil and rock samples from depth for laboratory analysis and classification.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Standard Penetration Test (SPT)",
      desc: "In-situ dynamic testing to measure soil resistance and estimate bearing capacity for foundation engineering.",
      icon: "📏",
    },
    {
      number: "03",
      title: "Core Sampling",
      desc: "Continuous undisturbed core recovery from rock formations to assess rock quality designation (RQD) and stratigraphy.",
      icon: "🔩",
    },
    {
      number: "04",
      title: "Laboratory Testing",
      desc: "Comprehensive soil classification, Atterberg limits, shear strength, consolidation, and permeability testing.",
      icon: "🔬",
    },
    {
      number: "05",
      title: "Piezometer Installation",
      desc: "Groundwater monitoring installations to track water table levels and pore pressure variations over time.",
      icon: "💧",
    },
    {
      number: "06",
      title: "Geotechnical Reporting",
      desc: "Detailed factual and interpretive reports with bore logs, test data, and engineering recommendations.",
      icon: "📋",
    },
  ];

  return (
    <div className="si-page" id="#soil-investigation">
      {/* Top Banner */}
      <section className="si-top-banner">
        <div className="si-top-banner__grid" />
        <div className="si-top-banner__glow" />
        <h1 className="si-top-banner__title">
          Soil <span className="si-text-accent">Investigation</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`si-overview ${isVisible["overview"] ? "si-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="si-overview__inner">
          <div className="si-overview__left">
            <div className="si-section-label">
              <span className="si-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="si-section-title">
              UNDERSTANDING THE
              <br />
              <span className="si-text-accent">GROUND BENEATH</span>
            </h2>
            <p className="si-overview__text">
              Before any structure is built, the ground must be understood. GSK Geotech's
              Soil Investigation service provides comprehensive subsurface characterisation
              through systematic borehole drilling, in-situ testing, and laboratory analysis.
            </p>
            <p className="si-overview__text">
              Our registered SBI (I&M)/SI certified team has executed soil investigation
              works across major LTA, PUB, HDB, and private projects as sub contactor, delivering accurate
              factual data and reliable geotechnical interpretation that engineers can
              confidently design from.
            </p>
            <div className="si-overview__highlights">
              {[
                "Registered SBI (I&M)/SI Contractor and SAC accreditation approved recently",
                "Experienced in Singapore's complex geology",
                "End-to-end: drilling, testing, reporting",
                "Compliant with SS EN & BS standards",
              ].map((item, i) => (
                <div key={i} className="si-overview__highlight-item">
                  <span className="si-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="si-overview__right">
            <div className="si-overview__image-block">
              <img
                src={overviewImg}
                alt="Borehole Drilling Soil Investigation Works"
                className="si-overview__image"
              />
              <div className="si-overview__stat-card">
                <span className="si-overview__stat-number">17+</span>
                <span className="si-overview__stat-text">SI PROJECTS<br />COMPLETED</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`si-methods ${isVisible["methods"] ? "si-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="si-methods__inner">
          <div className="si-section-label">
            <span className="si-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="si-section-title">
            INVESTIGATION
            <br />
            <span className="si-text-accent">METHODS</span>
          </h2>
          <p className="si-methods__subtitle">
            From surface to bedrock — we deploy a full suite of geotechnical investigation
            techniques tailored to each project's specific requirements.
          </p>
          <div className="si-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="si-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="si-method-card__number">{method.number}</div>
                <div className="si-method-card__icon">{method.icon}</div>
                <h3 className="si-method-card__title">{method.title}</h3>
                <p className="si-method-card__desc">{method.desc}</p>
                <div className="si-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`si-process ${isVisible["process"] ? "si-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="si-process__inner">
          <div className="si-section-label">
            <span className="si-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="si-section-title">
            OUR <span className="si-text-accent">PROCESS</span>
          </h2>
          <div className="si-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Preparation works, Permit approval, Mobilization, Prior to investigation." },
              { step: "02", title: "Drilling & Sampling", desc: "Systematic borehole advancement and sampling at regular intervals." },
              { step: "03", title: "In-Situ Testing", desc: "Field testing including SPT, Permeability." },
              { step: "04", title: "Lab Analysis", desc: "Soil and rock samples dispatched to accredited laboratories for classification and strength testing." },
              { step: "05", title: "Reporting", desc: "Factual report with bore logs, Test results, and interpretive geotechnical report with recommendations." },
            ].map((item, i) => (
              <div key={i} className="si-process__step">
                <div className="si-process__step-num">{item.step}</div>
                <div className="si-process__step-connector" />
                <div className="si-process__step-content">
                  <h4 className="si-process__step-title">{item.title}</h4>
                  <p className="si-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`si-projects ${isVisible["projects"] ? "si-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="si-projects__inner">
          <div className="si-section-label">
            <span className="si-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="si-section-title">
            COMPLETED SI
            <br />
            <span className="si-text-accent">PROJECTS</span>
          </h2>
          <div className="si-projects__grid">
            {[
              { title: "SI Work at CR102", client: "GeoMotion Singapore" },
              { title: "SI Works at Various Locations", client: "Neo Arch Engineering Pte Ltd" },
              { title: "SI Work at Farrer Park Road", client: "Neo Arch Engineering Pte Ltd" },
              { title: "SI Work at CR107", client: "GeoMotion Singapore" },
              { title: "SI Work at Tuas TTP2", client: "Geomotion Singapore" },
              { title: "SI Work at Various Locations", client: "Be Time Engineering Pte Ltd" },
              { title: "SI Work at 30 Jurong Port Road", client: "APS ASIA PDA Enterprises Pte Ltd" },
              { title: "SI Work at Woodlands", client: "Geonamics" },
              { title: "SI Work at Changi East", client: "Neo Arch Engineering Pte Ltd" },
              { title: "SI Work at Mandai - PUB", client: "Geo Motion Singapore" },
              { title: "SI Work at Woodlands Exyte", client: "Geo Motion Singapore" },
              { title: "K7066 DSTA Project", client: "Ryobi Geotechnic SGS" },
              { title: "Soil Investigation Work at Queenstown", client: "APS ASIA PDA Enterprises Pte Ltd" },
              { title: "Soil Investigation Work at Selatar South Street 1", client: "Ryobi Geotechnic SGS" },
              { title: "Soil Investigation Work at Tessensohn Road", client: "GIE Pte Ltd" },
              { title: "Soil Investigation Work at CR103", client: "Geolutions Singapore" },
              { title: "SI Work at Tanah Merah Coast Road", client: "Geo Motion Singapore" },
            ].map((proj, i) => (
              <div key={i} className="si-project-card">
                <h4 className="si-project-card__title">{proj.title}</h4>
                <p className="si-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="si-projects__cta">
            <a href="/projects" className="si-btn si-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="si-gallery">
        <div className="si-gallery__inner">
          <div className="si-section-label">
            <span className="si-section-label__line" />
            SITE PHOTOS
          </div>
          <h2 className="si-section-title">
            PROJECT <span className="si-text-accent">GALLERY</span>
          </h2>
          <div className="si-gallery__masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="si-gallery__item">
                <img src={img.src} alt={img.caption} className="si-gallery__img" />
                <div className="si-gallery__overlay">
                  <span className="si-gallery__caption">{img.caption}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="si-cta">
        <div className="si-cta__inner">
          <div className="si-cta__left">
            <p className="si-cta__label">READY TO START?</p>
            <h2 className="si-cta__title">
              NEED SOIL INVESTIGATION
              <br />
              <span className="si-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="si-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project requirements.
            </p>
          </div>
          <div className="si-cta__right">
            <a href="/contact-us" className="si-btn si-btn--primary si-btn--large">
              CONTACT US →
            </a>
            <div className="si-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoilInvestigation;